import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  getFirestore,
  query,
  setDoc,
  where,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';

import { RequestTimeoutError, showRequestTimeoutAlert } from '@/libs/errors/request-timeout';
import { captureException } from '@/libs/logger';
import { ensureOnline } from '@/libs/network';
import { getFirebaseApp } from '../init.web';
import { stripUndefinedDeep } from './sanitize-firestore-data';

// ─── Collection Constants ──────────────────────────────────────────────────────

export const FIRESTORE_COLLECTIONS = {
  COACH: 'coach',
  ACADEMY: 'academy',
  PLAYER: 'player',
  SESSION: 'session',
  EVENT: 'event',
  PLAYER_PATHWAY: 'playerpathway',
  PARENT: 'parent',
} as const;

const FIRESTORE_TIMEOUT_MS = 15000;

export { isFirestoreTimeoutError } from '@/libs/errors/request-timeout';

const withTimeout = async <T>(promise: Promise<T>, timeoutMs = FIRESTORE_TIMEOUT_MS) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race<T>([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          showRequestTimeoutAlert();
          reject(new RequestTimeoutError('Firestore', timeoutMs));
        }, timeoutMs);
      }),
    ]);
  } catch (err) {
    captureException(
      {
        error_payload: err,
      },
      { tags: { usk_log_type: 'firestore_error' } },
    );
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

// ─── Utility Types & Helpers ───────────────────────────────────────────────────

export type WithId<T> = T & { id: string };

export const snapshotToData = <T extends DocumentData>(
  snapshot: DocumentSnapshot<T>,
): WithId<T> | null => {
  if (!snapshot.exists) return null;
  return { id: snapshot.id, ...(snapshot.data() as T) };
};

export const snapshotsToData = <T extends DocumentData>(snapshot: QuerySnapshot<T>): WithId<T>[] =>
  snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...(docSnap.data() as T),
  }));

// ─── Generic Read Helpers ──────────────────────────────────────────────────────

export const fetchDoc = async <T extends DocumentData>(
  ref: DocumentReference<T>,
): Promise<WithId<T> | null> => {
  await ensureOnline();
  const snapshot = await withTimeout(getDoc(ref));
  return snapshotToData(snapshot);
};

export const fetchCollection = async <T extends DocumentData>(
  ref: CollectionReference<T> | Query<T>,
): Promise<WithId<T>[]> => {
  await ensureOnline();
  const snapshot = await withTimeout(getDocs(ref));
  return snapshotsToData(snapshot);
};

export const fetchDocFromServer = async <T extends DocumentData>(
  ref: DocumentReference<T>,
): Promise<WithId<T> | null> => {
  await ensureOnline();
  const snapshot = await getDocFromServer(ref);
  return snapshotToData(snapshot);
};

export const fetchCollectionFromServer = async <T extends DocumentData>(
  ref: CollectionReference<T> | Query<T>,
): Promise<WithId<T>[]> => {
  await ensureOnline();
  const snapshot = await getDocsFromServer(ref);
  return snapshotsToData(snapshot);
};

// ─── Collection & Document References ─────────────────────────────────────────

const db = () => getFirestore(getFirebaseApp());

export const coachCollection = <T extends DocumentData = DocumentData>() =>
  collection(db(), FIRESTORE_COLLECTIONS.COACH) as CollectionReference<T>;

export const coachDocRef = <T extends DocumentData = DocumentData>(coachId: string) =>
  doc(db(), FIRESTORE_COLLECTIONS.COACH, coachId) as DocumentReference<T>;

export const academyCollection = <T extends DocumentData = DocumentData>() =>
  collection(db(), FIRESTORE_COLLECTIONS.ACADEMY) as CollectionReference<T>;

export const academyDocRef = <T extends DocumentData = DocumentData>(academyId: string) =>
  doc(db(), FIRESTORE_COLLECTIONS.ACADEMY, academyId) as DocumentReference<T>;

export const playerCollection = <T extends DocumentData = DocumentData>() =>
  collection(db(), FIRESTORE_COLLECTIONS.PLAYER) as CollectionReference<T>;

export const playerDocRef = <T extends DocumentData = DocumentData>(playerId: string) =>
  doc(db(), FIRESTORE_COLLECTIONS.PLAYER, playerId) as DocumentReference<T>;

export const sessionCollection = <T extends DocumentData = DocumentData>() =>
  collection(db(), FIRESTORE_COLLECTIONS.SESSION) as CollectionReference<T>;

export const sessionDocRef = <T extends DocumentData = DocumentData>(sessionId: string) =>
  doc(db(), FIRESTORE_COLLECTIONS.SESSION, sessionId) as DocumentReference<T>;

export const eventCollection = <T extends DocumentData = DocumentData>() =>
  collection(db(), FIRESTORE_COLLECTIONS.EVENT) as CollectionReference<T>;

export const eventDocRef = <T extends DocumentData = DocumentData>(eventId: string) =>
  doc(db(), FIRESTORE_COLLECTIONS.EVENT, eventId) as DocumentReference<T>;

export const playerPathwayCollection = <T extends DocumentData = DocumentData>() =>
  collection(db(), FIRESTORE_COLLECTIONS.PLAYER_PATHWAY) as CollectionReference<T>;

// ─── Domain-Specific Helpers ───────────────────────────────────────────────────

// Coach
export const getCoach = <T extends DocumentData = DocumentData>(coachId: string) =>
  fetchDoc(coachDocRef<T>(coachId));

// Academy
export const getAcademy = <T extends DocumentData = DocumentData>(academyId: string) =>
  fetchDoc(academyDocRef<T>(academyId));

// Player
export const getPlayer = <T extends DocumentData = DocumentData>(playerId: string) =>
  fetchDoc(playerDocRef<T>(playerId));

export const getPlayerFromServer = <T extends DocumentData = DocumentData>(playerId: string) =>
  fetchDocFromServer(playerDocRef<T>(playerId));

// Session
export const getSession = <T extends DocumentData = DocumentData>(sessionId: string) =>
  fetchDoc(sessionDocRef<T>(sessionId));

// Event
export const getEvent = <T extends DocumentData = DocumentData>(eventId: string) =>
  fetchDoc(eventDocRef<T>(eventId));

export const getEventsBySessionId = <T extends DocumentData = DocumentData>(sessionId: string) =>
  fetchCollection(query(eventCollection<T>(), where('session_id', '==', sessionId)));

// PlayerPathway
export const getAllPlayerPathwayLevels = <T extends DocumentData = DocumentData>() =>
  fetchCollection(playerPathwayCollection<T>());

// ─── Generic Write Helpers ─────────────────────────────────────────────────────

export const updateDocMerge = async <T extends DocumentData>(
  ref: DocumentReference<T>,
  data: Partial<T>,
): Promise<void> => {
  await ensureOnline();
  const payload = stripUndefinedDeep(data) as T;
  await withTimeout(setDoc(ref, payload, { merge: true }));
};

// ─── Player Writes ─────────────────────────────────────────────────────────────

/**
 * Create `player/{playerId}` and return the created document.
 *
 * If `playerId` is not provided, Firestore auto-generates one.
 */
export const createPlayerCollection = async <T extends DocumentData = DocumentData>(
  data: T,
  playerId?: string,
): Promise<WithId<T> | null> => {
  await ensureOnline();
  const ref = (
    playerId ? playerDocRef<T>(playerId) : doc(playerCollection<T>())
  ) as DocumentReference<T>;

  await withTimeout(setDoc(ref, stripUndefinedDeep(data) as T));
  return await getPlayer<T>(ref.id);
};

/**
 * Update (merge) `player/{playerId}` and return the latest document.
 *
 * Note: This mirrors iOS `setData(..., merge: true)`. For concurrent note appends,
 * consider a transaction/arrayUnion if needed.
 */
export const updatePlayerCollection = async <T extends DocumentData = DocumentData>(
  playerId: string,
  data: Partial<T>,
): Promise<WithId<T> | null> => {
  const ref = playerDocRef<T>(playerId);
  await updateDocMerge(ref, data);
  return await getPlayer<T>(playerId);
};

export const updateCoachCollection = async <T extends DocumentData = DocumentData>(
  coachId: string,
  data: Partial<T>,
): Promise<WithId<T> | null> => {
  const ref = coachDocRef<T>(coachId);
  await updateDocMerge(ref, data);
  return await getCoach<T>(coachId);
};
